import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartUpdateEvent } from '@theme/events';

/**
 * Cart line item selling plan selector.
 *
 * Allows customers to change the delivery frequency of subscription items
 * or switch one-time items to a subscription, directly from the cart.
 *
 * @extends {Component}
 */
class CartSellingPlan extends Component {
  /**
   * Handles frequency dropdown change — updates the selling plan
   * on the existing line item via /cart/change.js.
   *
   * @param {Event} event
   */
  handleFrequencyChange(event) {
    const select = /** @type {HTMLSelectElement} */ (event.target);
    this.#updateSellingPlan(select.value);
  }

  /**
   * Handles CTA click to switch from one-time purchase to subscription.
   * Uses the first available selling plan.
   *
   * @param {PointerEvent} event
   */
  handleSwitchToSubscription(event) {
    const button = /** @type {HTMLElement} */ (event.target)?.closest('[data-first-selling-plan-id]');
    const sellingPlanId = button?.dataset.firstSellingPlanId;
    if (!sellingPlanId) return;
    this.#updateSellingPlan(sellingPlanId);
  }

  /**
   * Shows an error message using the existing cart-item error container.
   *
   * @param {Element|null|undefined} cartItemsComponent
   * @param {number} line
   */
  #showError(cartItemsComponent, line) {
    const errorText = cartItemsComponent?.querySelector(`[ref="cartItemError-${line}"]`);
    const errorContainer = cartItemsComponent?.querySelector(`[ref="cartItemErrorContainer-${line}"]`);

    if (errorText instanceof HTMLElement && errorContainer instanceof HTMLElement) {
      errorText.textContent = this.dataset.errorMessage || 'Something went wrong.';
      errorContainer.classList.remove('hidden');
    }
  }

  /**
   * Updates the selling plan for this line item via /cart/change.js.
   *
   * @param {string} sellingPlanId - The selling plan ID to set
   */
  async #updateSellingPlan(sellingPlanId) {
    const line = parseInt(this.dataset.line, 10);
    const quantity = parseInt(this.dataset.quantity, 10);

    if (!line || !quantity) return;

    const cartItemsComponent = this.closest('cart-items-component');
    const sectionId = cartItemsComponent?.dataset.sectionId;

    // Collect all cart-items-component section IDs for bundled section rendering
    const sectionsToUpdate = new Set();
    for (const el of document.querySelectorAll('cart-items-component')) {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        sectionsToUpdate.add(el.dataset.sectionId);
      }
    }

    // Show loading spinner, hide content via CSS
    this.classList.add('is-loading');
    this.setAttribute('aria-busy', 'true');

    // Disable cart interaction while the request is in-flight
    if (cartItemsComponent instanceof HTMLElement) {
      cartItemsComponent.setAttribute('inert', '');
    }

    const body = JSON.stringify({
      line,
      quantity,
      selling_plan: sellingPlanId,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    // Save previous select value so we can restore it on error
    const select = this.querySelector('.cart-selling-plan__select');
    const previousSelectValue = select
      ? Array.from(/** @type {HTMLSelectElement} */ (select).options).find((o) => o.defaultSelected)?.value
      : null;
    let succeeded = false;

    try {
      const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
      const data = await response.json();

      if (data.errors) {
        this.#showError(cartItemsComponent, line);
        return;
      }

      // Extract updated item count from the new section HTML
      let itemCount = 0;
      if (sectionId && data.sections?.[sectionId]) {
        const doc = new DOMParser().parseFromString(data.sections[sectionId], 'text/html');
        const countEl = doc.querySelector('[ref="cartItemCount"]');
        if (countEl) itemCount = parseInt(countEl.textContent, 10) || 0;
      }

      succeeded = true;

      // Announce success to screen readers
      this.#announceStatus(this.dataset.successMessage || 'Cart updated.');

      // Dispatch CartUpdateEvent — all cart-items-component instances
      // will receive this and morph their sections automatically
      this.dispatchEvent(
        new CartUpdateEvent(data, sectionId, {
          itemCount,
          source: 'cart-selling-plan',
          sections: data.sections,
        })
      );
    } catch (error) {
      console.error('[CartSellingPlan]', error);
      this.#showError(cartItemsComponent, line);
    } finally {
      // Restore previous select value on failure only; on success the morph may keep
      // the live select element, so resetting it would show the old frequency
      if (!succeeded && select && previousSelectValue != null) {
        /** @type {HTMLSelectElement} */ (select).value = previousSelectValue;
      }
      this.classList.remove('is-loading');
      this.removeAttribute('aria-busy');

      if (cartItemsComponent instanceof HTMLElement) {
        cartItemsComponent.removeAttribute('inert');
      }
    }
  }

  /**
   * Announces a status message to screen readers via a live region.
   *
   * @param {string} message
   */
  #announceStatus(message) {
    const el = document.createElement('span');
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    el.className = 'visually-hidden';
    el.textContent = message;
    document.body.appendChild(el);
    setTimeout(() => el.remove(), 1000);
  }
}

if (!customElements.get('cart-selling-plan')) {
  customElements.define('cart-selling-plan', CartSellingPlan);
}
